import React, { useId } from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlLabel } from "../FormControlWrapper/FormControlLabel";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { cn } from "@gettactic/helpers";
import {
  RadioInputProps,
  RadioInputPropsWithFormControl
} from "./RadioInput.types";

const radioHoverClassBase = "outline outline-[3px] outline-neutral-50";
const radioHoverClass = radioHoverClassBase
  .split(" ")
  .map((c) => `hover:${c} group-hover:${c}`)
  .join(" ");

const RadioInputInput = React.forwardRef<HTMLInputElement, RadioInputProps>(
  (props, ref) => {
    const {
      checked,
      onChange,
      disableOutline,
      radioPosition,
      className,
      disabled,
      ...rest
    } = props;

    return (
      <input
        ref={ref}
        type="radio"
        checked={checked}
        onChange={(e) => onChange?.(e.target.checked)}
        disabled={disabled}
        className={cn(
          "h-4 w-4 shrink-0 appearance-none rounded-full",
          "border border-neutral-400 bg-white",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "checked:border-[5px] checked:border-black",
          radioHoverClass,
          className
        )}
        {...rest}
      />
    );
  }
);

RadioInputInput.displayName = "RadioInputInput";

export const RadioInput = React.forwardRef<
  HTMLInputElement,
  RadioInputPropsWithFormControl
>((props, ref) => {
  const { radioPosition = "bottom", ...restProps } = props;
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(restProps);
  const {
    label,
    description,
    info,
    isRequired,
    isOptional,
    ...restWrapperProps
  } = wrapperProps;
  const { checked, onChange, disabled } = inputProps;

  const handleLabelClick = () => {
    if (!disabled && onChange && !checked) {
      onChange(true);
    }
  };

  // For left/right positions, we need a custom layout
  if (radioPosition === "left" || radioPosition === "right") {
    return (
      <div className="w-full">
        <div className="flex items-start justify-between gap-4">
          {radioPosition === "left" && (
            <div className="flex shrink-0 items-center pt-0.5">
              <RadioInputInput
                ref={ref}
                {...inputProps}
                radioPosition={radioPosition}
              />
            </div>
          )}
          <div
            className={cn(
              "flex min-w-0 flex-1 flex-col",
              !disabled && onChange && "cursor-pointer"
            )}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest(
                  '[role="tooltip"], [data-radix-tooltip-content], [data-radix-tooltip-trigger], button'
                )
              ) {
                return;
              }
              handleLabelClick();
            }}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={(e) => {
              if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleLabelClick();
              }
            }}
          >
            {label && (
              <FormControlLabel
                isRequired={isRequired}
                isOptional={isOptional}
                info={info}
              >
                {label}
              </FormControlLabel>
            )}
            {description && (
              <p className="text-md mt-1 text-justify text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {radioPosition === "right" && (
            <div className="flex w-12 shrink-0 items-center justify-end pt-0.5">
              <RadioInputInput
                ref={ref}
                {...inputProps}
                radioPosition={radioPosition}
              />
            </div>
          )}
        </div>
        {inputProps.disableOutline !== true && <FormControlOutline />}
      </div>
    );
  }

  // Default bottom position - use FormControlWrapper
  const radioId = useId();

  const handleLabelAreaClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest(
        '[role="tooltip"], [data-radix-tooltip-content], [data-radix-tooltip-trigger], button'
      )
    ) {
      return;
    }
    handleLabelClick();
  };

  const handleLabelAreaKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleLabelClick();
    }
  };

  return (
    <FormControlWrapper
      variant={"unstyled"}
      {...restWrapperProps}
      label={
        label ? (
          <div
            className={cn("w-full", !disabled && onChange && "cursor-pointer")}
            onClick={handleLabelAreaClick}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={handleLabelAreaKeyDown}
          >
            {label}
          </div>
        ) : undefined
      }
      description={
        description ? (
          <div
            className={cn(!disabled && onChange && "cursor-pointer")}
            onClick={handleLabelAreaClick}
            role="button"
            tabIndex={disabled ? undefined : 0}
            onKeyDown={handleLabelAreaKeyDown}
          >
            {description}
          </div>
        ) : undefined
      }
      info={info}
      isRequired={isRequired}
      isOptional={isOptional}
    >
      <RadioInputInput
        ref={ref}
        {...inputProps}
        radioPosition={radioPosition}
        id={radioId}
      />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

RadioInput.displayName = "RadioInput";
